import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { sharedGet } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { useLoadout } from "@/context/LoadoutContext";
import { Sparkles, Flame, ChevronRight } from "lucide-react";

export default function GamificationWidget() {
  const { t } = useT();
  const { palette } = useLoadout();
  const [data, setData] = useState(null);
  // Animate the progress bar from 0 to the actual pct on first render.
  const [barPct, setBarPct] = useState(0);
  // Tick the SP counter from 0 → actual value for a satisfying reveal.
  const [displaySp, setDisplaySp] = useState(0);

  useEffect(() => {
    sharedGet("/client/gamification/summary")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    if (data?.level?.pct != null) {
      // Delay one frame so the transition kicks in from 0 → pct.
      const id = requestAnimationFrame(() => setBarPct(data.level.pct));
      return () => cancelAnimationFrame(id);
    }
  }, [data]);

  // Count-up animation for the SP number.
  useEffect(() => {
    if (data?.total_sp == null) return;
    const target = data.total_sp;
    if (target === 0) { setDisplaySp(0); return; }
    const start = performance.now();
    const duration = 1200;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplaySp(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [data?.total_sp]);

  // Level up celebration — confetti + short haptic when the client's level
  // index goes up between mount and update. Persist last-seen level in
  // localStorage so we only celebrate a new level once per browser.
  const badgeRef = useRef(null);
  const [levelUp, setLevelUp] = useState(false);
  useEffect(() => {
    if (!data?.level?.current?.idx) return;
    const key = "solano_last_level_idx";
    const prev = parseInt(localStorage.getItem(key) || "0", 10);
    const cur = data.level.current.idx;
    if (prev && cur > prev) {
      setLevelUp(true);
      try { navigator.vibrate?.([20, 40, 20]); } catch {}
      // Fire confetti at the badge's on-screen position.
      const rect = badgeRef.current?.getBoundingClientRect();
      const origin = rect
        ? { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight }
        : { x: 0.2, y: 0.3 };
      confetti({
        particleCount: 90,
        spread: 70,
        origin,
        colors: ["#FC9700", "#FFD37E", "#FFB33D", "#2C5254"],
        scalar: 0.9,
        ticks: 200,
      });
      const t = setTimeout(() => setLevelUp(false), 2400);
      localStorage.setItem(key, String(cur));
      return () => clearTimeout(t);
    }
    if (!prev) localStorage.setItem(key, String(cur));
  }, [data?.level?.current?.idx]);

  if (!data) return null;

  const cur = data.level.current;
  const nxt = data.level.next;
  const nxtName = nxt ? (t(`rewards.states.${nxt.key}.name`) || nxt.key) : "";
  const curName = t(`rewards.states.${cur.key}.name`) || cur.key;

  return (
    <Link
      to="/app/premi"
      data-testid="gamification-widget"
      className="block rounded-3xl text-white p-5 relative overflow-hidden isolate shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 group"
      style={{
        backgroundImage: `linear-gradient(135deg, ${palette.from} 0%, ${palette.via} 50%, ${palette.to} 100%)`,
        WebkitMaskImage: "-webkit-radial-gradient(white, black)",
      }}
    >
      {/* Ambient blur + subtle rotating shine */}
      <div className="pointer-events-none absolute -top-10 -right-10 h-36 w-36 rounded-full blur-2xl" style={{ backgroundColor: palette.accent, opacity: 0.25 }} />
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_var(--mx,50%)_var(--my,50%),rgba(255,255,255,0.12),transparent_60%)]" />
      {/* Contrast scrim — keeps white text legible on themes with a pale
          mid-stop (Tricolore, Aurora, Rosé…). Darker in the middle where
          the pale stop lives. */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/25 via-black/35 to-black/25"
        aria-hidden
      />

      <div className="relative" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>
        <div className="flex items-center gap-3 mb-4">
          {/* Level badge — LV number integrated inside the emoji ring */}
          <div className="relative shrink-0" aria-hidden ref={badgeRef}>
            <div
              className={`h-16 w-16 rounded-full bg-white/10 backdrop-blur ring-2 ring-[#FC9700]/70 shadow-[0_0_20px_-4px_#FC9700] flex items-center justify-center relative transition-transform ${
                levelUp ? "animate-[popIn_1s_ease-out]" : ""
              }`}
            >
              <span className="text-3xl leading-none drop-shadow">{cur.emoji}</span>
              <span className={`absolute inset-x-0 -bottom-0.5 mx-auto w-max px-2 py-[1px] rounded-full bg-[#FC9700] text-[#1A1D1E] text-[10px] font-black tabular-nums shadow ring-2 ring-[#2C5254] ${
                levelUp ? "animate-[popIn_0.6s_ease-out_0.2s_backwards]" : ""
              }`}>
                LV{cur.idx}
              </span>
              {levelUp && (
                <span
                  className="pointer-events-none absolute inset-[-8px] rounded-full ring-4 ring-[#FFD37E]/70 animate-ping"
                  aria-hidden
                />
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-bold text-[#FFD37E]/90">
              {t("rewards.widgetTitle")}
            </div>
            <h3 className="font-heading text-xl font-bold truncate leading-tight drop-shadow-sm">
              {curName}
            </h3>
          </div>

          <div className="text-right shrink-0">
            <div className="font-heading text-3xl font-bold tabular-nums drop-shadow-sm">{displaySp}</div>
            <div className="text-[9px] uppercase tracking-widest text-white/60 font-bold">SP</div>
          </div>
        </div>

        {/* Progress bar with fill animation from 0 */}
        <div className="relative">
          <div className="h-2.5 w-full rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#FC9700] via-[#FFB33D] to-[#FFD37E] shadow-[0_0_10px_-2px_#FC9700] transition-[width] duration-[1400ms] ease-out"
              style={{ width: `${barPct}%` }}
              data-testid="gamification-progress-bar"
            />
          </div>
          {/* Traveling shimmer that plays once whenever pct changes */}
          <div
            key={`shimmer-${data.level.pct}`}
            className="pointer-events-none absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-white/25 to-transparent animate-[shimmer_1.6s_ease-out_forwards]"
            style={{ animationDelay: "200ms" }}
          />
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-3">
            {data.streak.current > 0 && (
              <span className="inline-flex items-center gap-1 text-white/90 font-semibold">
                <Flame className="h-3 w-3 text-[#FC9700]" /> {data.streak.current}d
              </span>
            )}
            {data.today_sp > 0 && (
              <span className="inline-flex items-center gap-1 text-[#FFD37E] font-semibold">
                <Sparkles className="h-3 w-3" /> +{data.today_sp}
              </span>
            )}
          </div>
          {nxt ? (
            <span className="text-white/85 tabular-nums font-medium">
              {data.level.sp_to_next} SP → <span className="text-[#FFD37E]">{nxtName}</span>
            </span>
          ) : (
            <span className="text-white/85 font-medium">{t("rewards.maxLevel")}</span>
          )}
        </div>

        <div className="mt-3 flex items-center justify-end text-[11px] font-semibold text-[#FC9700] group-hover:text-white transition">
          {t("rewards.widgetCta")} <ChevronRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}
